import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { TranslateApiService } from '../services/translate-api.service';
import { reportApiError } from './api-result.helper';

// English "Name" input paired with a Marathi translation input. The Marathi field auto-fills via
// MyMemory (see TranslateApiService) the first time the English field loses focus, but only
// while the Marathi field is still "untouched" — once it holds a value (typed by a user, loaded
// from an existing record, or a prior auto-translation), further edits to the English name won't
// silently overwrite it; a manual "Translate" tap is needed instead. That's what keeps this
// "automatic but editable" rather than fighting the person editing it.
@Component({
  selector: 'app-bilingual-name-field',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="row">
      <div class="col-md-6 form-group">
        <label class="required">{{ label }}</label>
        <input class="form-control" [placeholder]="placeholder" [(ngModel)]="name" (ngModelChange)="nameChange.emit(name)" (blur)="onNameBlur()" />
      </div>
      <div class="col-md-6 form-group">
        <label>{{ label }} (मराठी)</label>
        <div class="input-group">
          <input
            class="form-control bilingual-mr-input"
            placeholder="मराठी नाव"
            [(ngModel)]="nameMr"
            (ngModelChange)="onNameMrEdited()"
          />
          <div class="input-group-append">
            <button
              class="btn btn-primary bilingual-translate-btn"
              type="button"
              title="Translate from English"
              [disabled]="translating || !name.trim()"
              (click)="translateNow()"
            >
              <span class="spinner-border spinner-border-sm" *ngIf="translating"></span>
              <i class="fa fa-language" *ngIf="!translating"></i>
              <span class="bilingual-translate-label">Translate</span>
            </button>
          </div>
        </div>
        <small class="text-muted" *ngIf="translating">Translating…</small>
      </div>
    </div>
  `,
  styles: [
    `
      // The translate button used to be a plain outline-secondary icon button — same gray as the
      // input border it's attached to, easy to miss as an actual control rather than decoration.
      // A solid brand-colored button with a label makes it read as clickable everywhere this
      // shared field is used (Machines, Products, Machine Types, …), not just this one component.
      .bilingual-translate-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 700;
        white-space: nowrap;
      }

      .bilingual-translate-label {
        @media (max-width: 480px) {
          display: none;
        }
      }

      .bilingual-mr-input {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }
    `,
  ],
})
export class BilingualNameFieldComponent implements OnChanges {
  @Input() label = 'Name';
  @Input() placeholder = 'Name';
  @Input() name = '';
  @Output() nameChange = new EventEmitter<string>();
  @Input() nameMr = '';
  @Output() nameMrChange = new EventEmitter<string>();

  translating = false;
  private mrTouched = false;

  private translateApi = inject(TranslateApiService);
  private ngZone = inject(NgZone);
  private toastr = inject(ToastrService);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['nameMr']) {
      this.mrTouched = !!this.nameMr?.trim();
    }
  }

  onNameMrEdited() {
    this.mrTouched = true;
    this.nameMrChange.emit(this.nameMr);
  }

  onNameBlur() {
    if (this.mrTouched) return;
    this.translateNow();
  }

  async translateNow() {
    if (!this.name.trim() || this.translating) return;
    this.translating = true;
    const res = await this.translateApi.toMarathi(this.name.trim());
    // Same zone-continuation issue documented across this app's other load()/save() methods — the
    // await can resume outside Angular's zone, so without this the translation lands in `nameMr`
    // internally but the input never visually updates, making auto-translate-on-blur look like it
    // silently does nothing.
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
        if (res.Data.translation) {
          this.nameMr = res.Data.translation;
          this.nameMrChange.emit(this.nameMr);
        }
      } else {
        reportApiError(this.toastr, res);
      }
      this.translating = false;
    });
  }
}
